import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, createBlock as _createBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, normalizeClass as _normalizeClass, withCtx as _withCtx, createCommentVNode as _createCommentVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "grid grid-cols-2 lg:grid-cols-3 gap-3 p-4" }
const _hoisted_2 = { class: "flex items-start gap-3" }
const _hoisted_3 = { class: "p-2 rounded-lg bg-muted shrink-0" }
const _hoisted_4 = { class: "flex-1 min-w-0" }
const _hoisted_5 = { class: "font-medium text-sm" }
const _hoisted_6 = { class: "text-xs text-muted-foreground mt-1 line-clamp-2" }
const _hoisted_7 = {
  key: 0,
  class: "flex flex-wrap gap-1 mt-2"
}

import {
  Target,
  Network,
  FileCode,
  BarChart3,
  ClipboardList,
  SearchCode,
  BookOpen,
  Sparkles,
} from 'lucide-vue-next'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import type { CopilotTemplate, CopilotTemplateCategory } from '@/types'


export default /*@__PURE__*/_defineComponent({
  __name: 'TemplateSelector',
  emits: ["select"],
  setup(__props, { emit: __emit }) {

const emit = __emit

const COPILOT_TEMPLATES: CopilotTemplate[] = [
  {
    id: 'scope',
    category: 'scope',
    title: 'Documento de Alcance',
    description: 'Genera un documento de alcance con objetivos, limites y entregables',
    promptTemplate:
      'Genera un documento de alcance completo para el proyecto basandote en el contexto proporcionado. Incluye: Objetivos, Alcance (dentro y fuera), Entregables, Supuestos, Restricciones y Criterios de Aceptacion.',
    icon: 'Target',
    requiredContextTypes: ['requirement'],
  },
  {
    id: 'architecture',
    category: 'architecture',
    title: 'Documento de Arquitectura',
    description: 'Genera un documento de arquitectura tecnica con diagramas y decisiones',
    promptTemplate:
      'Genera un documento de arquitectura tecnica basandote en las aplicaciones y requerimientos del proyecto. Incluye: Vision General, Componentes, Tecnologias, Integraciones, Decisiones de Diseño y Diagramas (usa mermaid para diagramas).',
    icon: 'Network',
    requiredContextTypes: ['application', 'requirement'],
  },
  {
    id: 'specifications',
    category: 'specifications',
    title: 'Especificaciones Tecnicas',
    description: 'Genera especificaciones tecnicas detalladas a partir de los requerimientos',
    promptTemplate:
      'Genera un documento de especificaciones tecnicas detalladas. Para cada requerimiento seleccionado, incluye: Descripcion funcional, Flujos de usuario, Reglas de negocio, Criterios de aceptacion, y Consideraciones tecnicas.',
    icon: 'FileCode',
    requiredContextTypes: ['requirement'],
  },
  {
    id: 'status_report',
    category: 'status_report',
    title: 'Reporte de Estado',
    description: 'Genera un reporte de estado del proyecto con metricas y progreso',
    promptTemplate:
      'Genera un reporte de estado del proyecto. Incluye: Resumen Ejecutivo, Progreso de Requerimientos (por estado), Metricas clave, Riesgos identificados, Proximos pasos y Reuniones recientes.',
    icon: 'BarChart3',
    requiredContextTypes: ['requirement', 'meeting'],
  },
  {
    id: 'meeting_minutes',
    category: 'meeting_minutes',
    title: 'Acta de Reunion',
    description: 'Genera un acta de reunion formateada a partir de notas y decisiones',
    promptTemplate:
      'Genera un acta de reunion formal basandote en la informacion de reuniones proporcionada. Incluye: Datos de la reunion, Asistentes, Agenda, Puntos tratados, Decisiones tomadas, Accionables con responsables y fechas.',
    icon: 'ClipboardList',
    requiredContextTypes: ['meeting'],
  },
  {
    id: 'technical_review',
    category: 'technical_review',
    title: 'Revision Tecnica',
    description: 'Genera un reporte de revision tecnica de aplicaciones',
    promptTemplate:
      'Genera un reporte de revision tecnica de las aplicaciones del proyecto. Incluye: Estado actual, Stack tecnologico, Version actual, Deuda tecnica identificada, Recomendaciones de mejora, y Plan de actualizacion.',
    icon: 'SearchCode',
    requiredContextTypes: ['application'],
  },
  {
    id: 'manual',
    category: 'manual',
    title: 'Manual de Usuario',
    description: 'Genera un manual de usuario basado en los requerimientos y aplicaciones',
    promptTemplate:
      'Genera un manual de usuario para la aplicacion. Incluye: Introduccion, Requisitos, Guia de inicio rapido, Funcionalidades principales (basado en requerimientos), Preguntas frecuentes.',
    icon: 'BookOpen',
    requiredContextTypes: ['requirement', 'application'],
  },
  {
    id: 'custom',
    category: 'custom',
    title: 'Documento Personalizado',
    description: 'Conversa libremente con el copiloto para generar cualquier documento',
    promptTemplate: '',
    icon: 'Sparkles',
  },
]

const iconMap: Record<string, unknown> = {
  Target,
  Network,
  FileCode,
  BarChart3,
  ClipboardList,
  SearchCode,
  BookOpen,
  Sparkles,
}

const getIcon = (iconName: string) => {
  return iconMap[iconName] || Sparkles
}

const getCategoryColor = (category: CopilotTemplateCategory): string => {
  const colors: Record<CopilotTemplateCategory, string> = {
    scope: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    architecture: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    specifications: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    manual: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    status_report: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    meeting_minutes: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    technical_review: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    custom: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  }
  return colors[category] || colors.custom
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createElementBlock(_Fragment, null, _renderList(COPILOT_TEMPLATES, (template) => {
      return _createVNode(_unref(Card), {
        key: template.id,
        class: _normalizeClass(["cursor-pointer transition-all hover:shadow-md hover:border-primary/50", { 'col-span-2 lg:col-span-3 border-dashed': template.id === 'custom' }]),
        onClick: ($event: any) => (emit('select', template))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(CardContent), { class: "p-4" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  (_openBlock(), _createBlock(_resolveDynamicComponent(getIcon(template.icon)), { class: "h-5 w-5" }))
                ]),
                _createElementVNode("div", _hoisted_4, [
                  _createElementVNode("p", _hoisted_5, _toDisplayString(template.title), 1),
                  _createElementVNode("p", _hoisted_6, _toDisplayString(template.description), 1),
                  (template.requiredContextTypes?.length)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(template.requiredContextTypes, (ctx) => {
                          return (_openBlock(), _createBlock(_unref(Badge), {
                            key: ctx,
                            variant: "secondary",
                            class: _normalizeClass([getCategoryColor(template.category), "text-[10px] px-1.5 py-0"])
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(ctx), 1)
                            ]),
                            _: 2
                          }, 1032, ["class"]))
                        }), 128))
                      ]))
                    : _createCommentVNode("", true)
                ])
              ])
            ]),
            _: 2
          }, 1024)
        ]),
        _: 2
      }, 1032, ["class", "onClick"])
    }), 64))
  ]))
}
}

})